import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { type PageLayoutStore } from 'shared/layout'
import { logger, removeSpacesFromNumericString, toLocalISOString } from 'shared/lib'
import type { ICardFilter } from 'shared/ui'
import { TableStore } from 'shared/ui/Table'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { RequestLoadData } from 'shared/api/lib'
import { type IKeyword } from 'entities/Keywords'
import { KeywordsApi } from 'entities/Keywords/api/keywords'
import { Keyword } from 'entities/Keywords/model/Keyword'
import { ContactStatistic, type IParamsGetContactsStatistic } from 'entities/ContactStatistic'
import type {
  IParamsGetContacts,
  IResponseGetKeywordContactsStatistic,
} from 'entities/Keywords/api/type'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { KeywordsRoutes } from 'pages/keywords/route/type'

export class KeywordDetailsStore {
  private readonly _keywordId: string | undefined
  private _keyword: IKeyword | null = null
  private _isLoading = true
  private _page = 1
  private _limit = 10
  private _total = 0
  private _search = ''
  private _filter: IParamsGetContactsStatistic['filter'] = 'sent_to'

  private readonly _pageLayoutStore: PageLayoutStore
  private _contactStatisticsMap: Map<number, ContactStatistic> = new Map()
  private _contactsDetailsManageStore: ContactsDetailsManageStore
  private _contactStatisticsTableStore = new TableStore<ContactStatistic>({
    element: 'contact',
    withoutDefaultManageColumns: true,
  })
  private _contactsTagsModalStore: ContactsTagsModalStore
  private _calendarFilterStore = new CalendarFilterStore()

  private _disposeFetchContacts: IReactionDisposer | null = null
  private _disposeChangeParams: IReactionDisposer | null = null
  private _previousSearchValue = ''

  constructor(
    keywordId: string | undefined,
    pageLayoutStore: PageLayoutStore,
    private _entities: IEntitiesStore
  ) {
    this._keywordId = keywordId
    this._pageLayoutStore = pageLayoutStore
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    this._contactsTagsModalStore = new ContactsTagsModalStore(this._entities)

    makeAutoObservable(this)

    this.reactionFetchContacts()
    this.reactionChangeParams()
    void this._contactsDetailsManageStore.init()
  }

  reactionChangeParams = () => {
    this._disposeChangeParams?.()
    this._disposeChangeParams = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._requestLoadContacts.setLoading(true)
        this._page = 1
      }
    )
  }

  reactionFetchContacts = () => {
    this._disposeFetchContacts?.()
    this._disposeFetchContacts = reaction(
      () => this.params,
      () => this._requestLoadContacts.debounceLoadData()
    )
  }

  dispose = () => {
    this._disposeFetchContacts?.()
    this._contactsDetailsManageStore.reset()
    this._disposeChangeParams?.()
    this._requestTrackSearchActivity.dispose()
    this._requestLoadContacts.dispose()
  }

  get isLoading() {
    return this._isLoading
  }

  get isContactLoading() {
    return this._requestLoadContacts.loading
  }

  get keyword() {
    return this._keyword
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get params() {
    return {
      page: this._page,
      ...this.paramsWithoutPage,
    }
  }

  get filter() {
    return this._filter
  }

  get search() {
    return this._search
  }

  get total() {
    return this._total
  }

  get page() {
    return this._page
  }

  get limit() {
    return this._limit
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  get paramsWithoutPage(): IParamsGetContacts {
    return {
      term: removeSpacesFromNumericString(this._search),
      limit: this._limit,
      filter: this._filter === 'sent_to' ? null : this._filter,
      from: toLocalISOString(this.calendarFilterStore.sendDate?.from ?? null) as string,
      to: toLocalISOString(this.calendarFilterStore.sendDate?.to ?? null) as string,
    }
  }

  get contactStatisticsItems() {
    return Array.from(this._contactStatisticsMap.values())
  }

  get statistics() {
    return this._keyword?.statistics
  }

  get filters() {
    const filters = this.statistics?.getFilters({
      sent_to: {
        tooltipLabel: 'Contacts who subscribed to this keyword',
        title: 'Subscribers',
      },
    })
    const relevantColumnsIds = ['sent_to', 'subscribed', 'opted_out']

    return filters?.filter((column) => relevantColumnsIds.includes(column.key)) as ICardFilter<
      IParamsGetContactsStatistic['filter']
    >[]
  }

  get contactStatisticsTableStore() {
    return this._contactStatisticsTableStore
  }

  get isNoContactData() {
    return !this.isContactLoading && !this._contactStatisticsMap.size && !this.search
  }

  get calendarFilterStore() {
    return this._calendarFilterStore
  }

  get contactsTagsModalStore() {
    return this._contactsTagsModalStore
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        this._contactsDetailsManageStore.onOpenContactDetails(
          {
            contactId: data.contactId,
            inboxId: this.keyword?.inboxId,
          },
          'keywords',
          true
        )
      },
    }
  }

  init = async () => {
    try {
      const { data } = await KeywordsApi.getKeywordById(this._keywordId as string)
      this._keyword = new Keyword(data, this._entities)
      this.calendarFilterStore.updateCreatedAt(this._keyword?.createdAt)
      this.calendarFilterStore.setFromFilterDefaultDate()

      void this.fetchContacts()
    } catch (e) {
      logger.error(e)

      uiStore.changeRoute({
        path: `/${KeywordsRoutes.root}`,
      })
    } finally {
      runInAction(() => {
        this._isLoading = false
      })
    }
  }

  private _requestTrackSearchActivity = new RequestLoadData<string>({
    debounceDelay: 2000,
    requestCallback: async (_config, arg) => {
      if (typeof arg !== 'string') return

      this._entities.organizationStore.handleTrackOrganizationActivity({
        event: 'phi.contact.searched',
        data: {
          search_query: arg,
        },
      })
    },
  })

  private _requestLoadContacts = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      if (!this._keyword) return

      const { data } = await KeywordsApi.getKeywordContacts(this._keyword.id, this.params, {
        cancelToken: cancelRequest.token,
      })

      if (
        this.search !== this._previousSearchValue &&
        this._entities.organizationStore.isActivityLoggingEnabled
      ) {
        this._previousSearchValue = this.search
        this._requestTrackSearchActivity.debounceLoadData(this.search)
      }

      this.setContactsData(data)
    },
  })

  fetchContacts = async () => {
    await this._requestLoadContacts.loadData()
  }

  setContactsData = ({ data, meta }: IResponseGetKeywordContactsStatistic) => {
    this._contactStatisticsMap.clear()
    this._total = meta.total
    this._page = meta.current_page

    data.forEach((contactStatistics) => {
      this._contactStatisticsMap.set(contactStatistics.id, new ContactStatistic(contactStatistics))
    })
  }

  resetSearchAndFilters = () => {
    this.setSearch('')
    this.calendarFilterStore.resetSearchAndFilters()
  }

  onActiveFilter = (filter: ICardFilter<IParamsGetContactsStatistic['filter']>) => {
    this._filter = filter.key

    this.resetSearchAndFilters()
  }

  setSearch = (value: string) => {
    this._search = value
  }

  setIsLoading = (isLoading: boolean) => {
    this._isLoading = isLoading
  }

  onPaginationTableChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
  }

  onSuccessDelete = () => {
    this.contactStatisticsTableStore.unselectAllIds()
    void this.fetchContacts()
  }
}
